import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../../../../hooks/customDeviceDetect";
import { cn } from "../../../../util/classNameConstructor";
import SurveyGuideAnimation from "../../../atoms/animations/SurveyGuideAnimation";
import Transition from "../../../atoms/animations/Transition";
import Button from "../../../core/button/Button";
import type { PageType } from "../survey.config";

interface TakeSurveyGuideProps {
  name: PageType;
  onRefresh?: boolean;
  hide?: boolean;
  onClose?: () => void;
  customContent?: JSX.Element; // Define customContent prop
  classNames?: {
    trigger?: string;
    content?: string;
    container?: string;
  };
}

const SurveyGuide: React.FC<TakeSurveyGuideProps> = ({
  name,
  onRefresh,
  hide,
  onClose,
  customContent,
  classNames,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [show, setShow] = useState(false);
  const [localstorageData, setLocalstorageData] = useState(null); // initialize with false

  const getLocalStorageGuide = () => {
    const stored = localStorage.getItem("guide");
    try {
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error("Parsing error:", error);
      return {};
    }
  };

  const setGuideProperty = (key, value) => {
    const guide = getLocalStorageGuide();
    guide[key] = value;
    localStorage.setItem("guide", JSON.stringify(guide));
  };

  useEffect(() => {
    if (localstorageData !== null) {
      setShow(false);

      if (localstorageData?.[name] !== "true") {
        setTimeout(() => {
          setShow(true);
        }, 1000);
      }
    }
  }, [localstorageData]);

  useEffect(() => {
    if (typeof Storage !== "undefined") {
      setLocalstorageData(getLocalStorageGuide());
    }
  }, [onRefresh]);

  useEffect(() => {
    if (hide) {
      setShow(false);
    }
  }, [hide]);

  const pageContent = {
    point: {
      animation: <SurveyGuideAnimation type={name} />,
      title: t("util:set", { val: t("global:points") }),
      description: t("map:drawInfoTooltip.point"),
    },
    path: {
      animation: <SurveyGuideAnimation type={name} />,
      title: t("util:set", { val: t("global:paths") }),
      description: t("map:drawInfoTooltip.path"),
    },
    area: {
      animation: <SurveyGuideAnimation type={name} />,
      title: t("util:set", { val: t("global:areas") }),
      description: t("map:drawInfoTooltip.area"),
    },
  };

  const content = pageContent[name];

  // If the content doesn't exist for the given name, return null
  if (!content && !customContent) {
    return null;
  }

  return (
    <div className={classNames?.container}>
      <Button
        variant="primary"
        size="lg"
        leadingIcon="question-mark"
        onClick={() => setShow(!show)}
        classNameWrapper={classNames?.trigger}
      />
      <Transition
        show={show}
        from={{
          y: 50,
          x: 0,
          scale: 0.5,
          transformOrigin:
            document.dir === "rtl" ? "bottom right" : "bottom left",
          rotate: "0deg",
        }}
        leave={{
          y: 50,
          x: 0,
          scale: 0.7,
          transformOrigin:
            document.dir === "rtl" ? "bottom right" : "bottom left",
          rotate: "10deg",
        }}
        enter={{
          y: 0,
          x: 0,
          scale: 1,
          transformOrigin:
            document.dir === "rtl" ? "bottom right" : "bottom left",
          rotate: "0deg",
        }}
        easing="stiff"
        style={{
          position: "absolute",
          height: "auto",
          width: isMobile ? "100%" : "390px",
          maxWidth: "calc(100vw - 2rem)",
          insetInlineStart: "0",
          bottom: "4rem",
          display: "flex",
          zIndex: 999,
          pointerEvents: "none",
        }}
      >
        <div
          className={cn(
            "bg-surface-highlight-default  pointer-events-auto relative flex max-h-[80vh] w-[390px] flex-col justify-center overflow-hidden rounded-2xl",
            classNames?.content,
          )}
          style={{
            boxShadow:
              "var(--shadows-shadow-2-xl-box-shadow, 0px 16px 50px -6px rgba(77, 72, 67, 0.15), 0px 8px 25px 0px rgba(77, 72, 67, 0.03))",
            width: "calc(100% - 4rem)",
          }}
        >
          {content?.animation && (
            <div className="[&_path]:fill-text-on-primary -mb-10 mt-2 flex w-full items-center justify-center">
              {content.animation}
            </div>
          )}
          <div className="relative flex shrink-0 flex-col items-center justify-start gap-3 p-5 sm:gap-8 sm:p-8">
            {customContent || (
              <div className="text-text-on-primary relative flex shrink-0 flex-col items-center justify-center gap-1 self-stretch">
                <p className="body-md-bold text-center">{content.title}</p>
                <p className="body-md whitespace-pre-wrap text-center">
                  {content.description}
                </p>
              </div>
            )}
            <Button
              variant="white"
              text={t("global:understood")}
              onClick={() => {
                // onClose(name);
                setGuideProperty(name, "true");
                setShow(false);
              }}
            />
          </div>
        </div>
      </Transition>
    </div>
  );
};

export default SurveyGuide;
